import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  FormControl,
  FormLabel,
  FormGroup,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  FaFileUpload,
  FaDownload,
  FaLanguage,
  FaTags,
  FaGlobeAmericas,
  FaGlobeEurope,
  FaInfo,
  FaBarcode,
  FaFilePdf
} from 'react-icons/fa';
import { MdCheckCircle, MdRadioButtonUnchecked, MdTranslate } from 'react-icons/md';
import { CloudUpload, CheckCircle, Clear, Description, Visibility } from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL, { API_ENDPOINTS, apiCall } from '../config/api';
import { formatLogTime } from '../utils/dateUtils';
import { NotificationContext } from '../context/NotificationContext';
import FavoriteButton from '../components/FavoriteButton';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const BilingualLabels = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs,
    addLog: contextAddLog,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  // Refs for async processing
  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // Helper function to add logs to activity context
  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        if (processIdRef.current) {
          clearIntervalRef(processIdRef.current);
        }
      }
    };
  }, [clearIntervalRef]);

  const [selectedTab, setSelectedTab] = useState(0); // 0 = US, 1 = EU
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);

  // US Tab State
  const [usCountries, setUsCountries] = useState({
    argentina: false,
    belgium: false,
    china: false,
    cambodia: false,
    egypt: false,
    india: false,
    turkey: false,
    ukraine: false,
    vietnam: false
  });

  const [usLanguage, setUsLanguage] = useState({
    trilingual: false,
    english: false
  });

  const [usCareInstructions, setUsCareInstructions] = useState({
    washableNonHeat: false,
    washable: false,
    indoor: false,
    indoorOutdoor: false,
    outdoorReversible: false,
    indoorReversible: false
  });

  // Track enabled state for US checkboxes
  const [usEnabled, setUsEnabled] = useState({
    trilingual: false,
    english: false,
    washableNonHeat: false,
    washable: false,
    indoor: false,
    indoorOutdoor: false,
    outdoorReversible: false,
    indoorReversible: false
  });

  // EU Care Instructions
  const [euCareInstructions, setEuCareInstructions] = useState({
    washableNonHeat: false,
    washable: false,
    indoor: false,
    indoorOutdoor: false,
    outdoorReversible: false
  });

  // Effect to update enabled states based on selections
  // Matching desktop BilingualLabels.cs checkbox logic exactly
  useEffect(() => {
    if (selectedTab === 0) { // US Tab
      const hasCountries = Object.values(usCountries).some(Boolean);
      const hasTrilingual = usLanguage.trilingual;
      const hasEnglish = usLanguage.english;

      // Desktop logic for Outdoor Reversible:
      // - Only enabled when TRILINGUAL is selected (NOT English)
      // - AND (Washable OR Washable Non Heat Dry OR Indoor/Outdoor is checked)
      const shouldEnableOutdoorReversible = hasTrilingual && !hasEnglish && (
        usCareInstructions.washable ||
        usCareInstructions.washableNonHeat ||
        usCareInstructions.indoorOutdoor
      );

      // Desktop logic for Indoor Reversible:
      // - Only enabled when Indoor (chkBackLabel) is checked
      const shouldEnableIndoorReversible = (hasTrilingual || hasEnglish) && usCareInstructions.indoor;

      setUsEnabled({
        // Language options enabled if any country is selected
        trilingual: hasCountries,
        english: hasCountries,
        // Care instructions based on language selection (matching desktop)
        // TRILINGUAL enables: washable, washableNonHeat, indoor, indoorOutdoor
        // ENGLISH enables: indoor, indoorOutdoor ONLY (NOT washable options)
        washableNonHeat: hasTrilingual && !hasEnglish,
        washable: hasTrilingual && !hasEnglish,
        indoor: hasTrilingual || hasEnglish,
        indoorOutdoor: hasTrilingual || hasEnglish,
        outdoorReversible: shouldEnableOutdoorReversible,
        indoorReversible: shouldEnableIndoorReversible
      });
    }
  }, [selectedTab, usCountries, usLanguage, usCareInstructions.indoor, usCareInstructions.washable, usCareInstructions.washableNonHeat, usCareInstructions.indoorOutdoor]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // US Country change handler with dependencies
  // Desktop logic: Countries are MUTUALLY EXCLUSIVE - selecting one unchecks all others
  const handleUsCountryChange = (country) => {
    setUsCountries(prev => {
      const isChecking = !prev[country];

      if (isChecking) {
        // Uncheck all other countries (matching desktop behavior exactly)
        const newState = {
          argentina: false,
          belgium: false,
          china: false,
          cambodia: false,
          egypt: false,
          india: false,
          turkey: false,
          ukraine: false,
          vietnam: false
        };
        newState[country] = true;
        return newState;
      } else {
        // Just uncheck this country
        return { ...prev, [country]: false };
      }
    });
  };

  // US Language change handler with dependencies
  const handleUsLanguageChange = (language) => {
    setUsLanguage(prev => {
      if (language === 'trilingual') {
        return { trilingual: !prev.trilingual, english: false };
      } else {
        return { trilingual: false, english: !prev.english };
      }
    });

    // Reset care instructions when switching to ENGLISH
    if (language === 'english' && !usLanguage.english) {
      setUsCareInstructions(prev => ({
        ...prev,
        washable: false,
        washableNonHeat: false,
        outdoorReversible: false // Outdoor Reversible should NEVER be enabled for English
      }));
    }
  };

  // US Care Instruction change handler with dependencies
  // Matching desktop BilingualLabels.cs checkbox logic exactly
  const handleUsCareInstructionChange = (instruction) => {
    setUsCareInstructions(prev => {
      const newState = { ...prev };
      const isChecking = !prev[instruction];

      if (isChecking) {
        // Desktop logic: checking one care instruction unchecks specific others
        switch(instruction) {
          case 'washable':
            // Desktop: chkWashable_CheckedChanged
            newState.washableNonHeat = false;
            newState.indoor = false;
            newState.indoorOutdoor = false;
            newState.indoorReversible = false;
            // Outdoor Reversible stays enabled (desktop enables chkOutDoorReverse)
            break;
          case 'washableNonHeat':
            // Desktop: chkMWashableDry_CheckedChanged
            newState.washable = false;
            newState.indoor = false;
            newState.indoorOutdoor = false;
            newState.indoorReversible = false;
            // Outdoor Reversible stays enabled
            break;
          case 'indoor':
            // Desktop: chkBackLabel_CheckedChanged
            newState.washable = false;
            newState.washableNonHeat = false;
            newState.indoorOutdoor = false;
            newState.outdoorReversible = false; // Desktop: chkOutDoorReverse.Checked = false and Enabled = false
            // Indoor Reversible will be enabled by useEffect
            break;
          case 'indoorOutdoor':
            // Desktop: chkOutdoor_CheckedChanged
            newState.washable = false;
            newState.washableNonHeat = false;
            newState.indoor = false;
            newState.indoorReversible = false;
            // Outdoor Reversible will be enabled by useEffect (only if TRILINGUAL, not ENGLISH)
            break;
          case 'outdoorReversible':
            // No specific unchecking in desktop
            break;
          case 'indoorReversible':
            // No specific unchecking in desktop
            break;
        }
      } else {
        // When unchecking, handle disabling of Outdoor Reversible
        // Desktop: if (!chkBackLabel.Checked && !chkMWashableDry.Checked && !chkWashable.Checked && !chkOutdoor.Checked)
        switch(instruction) {
          case 'washable':
          case 'washableNonHeat':
          case 'indoorOutdoor':
            // Check if no enabling conditions remain for Outdoor Reversible
            const remainingWashable = instruction !== 'washable' && newState.washable;
            const remainingWashableNonHeat = instruction !== 'washableNonHeat' && newState.washableNonHeat;
            const remainingIndoorOutdoor = instruction !== 'indoorOutdoor' && newState.indoorOutdoor;

            if (!remainingWashable && !remainingWashableNonHeat && !remainingIndoorOutdoor) {
              newState.outdoorReversible = false;
            }
            break;
          case 'indoor':
            // When unchecking indoor, disable indoor reversible
            newState.indoorReversible = false;
            break;
        }
      }

      newState[instruction] = isChecking;
      return newState;
    });
  };

  // EU Care Instruction change handler (mutually exclusive)
  const handleEuCareInstructionChange = (instruction) => {
    setEuCareInstructions(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[instruction] = !prev[instruction];
      return newState;
    });
  };

  // File handling
  const onDrop = useCallback((acceptedFiles) => {
    const excelFiles = acceptedFiles.filter(file =>
      file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.csv') ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'text/csv'
    );

    if (excelFiles.length !== acceptedFiles.length) {
      showNotification('Only Excel (.xlsx) and CSV files are supported', 'warning');
    }

    if (excelFiles.length === 0) return;

    const newFiles = excelFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      status: 'ready'
    }));

    // Replace existing files instead of adding (only one file at a time)
    setFiles(newFiles);
    setError('');
  }, [showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    multiple: false
  });

  const removeFile = (id) => {
    setFiles(files => files.filter(f => f.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadTemplate = async (type) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      const response = await axios.get(`${API_BASE_URL}/api/bilingual-labels/download-template/${type}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_Labels_Template_${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Template downloaded successfully');
    } catch (error) {
      setError('Failed to download template');
      console.error('Download error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch status from server and update activity log
  const fetchStatus = async (currentProcessId, activityProcessId, uiProcessId) => {
    try {
      const response = await apiCall(API_ENDPOINTS.BILINGUAL_LABELS_STATUS(currentProcessId), {
        method: 'GET',
      });

      // Update activity log with server logs
      if (response.logs && response.logs.length > 0) {
        setLogs(activityProcessId, response.logs);
      }

      // Update progress
      if (response.progressPercentage !== undefined) {
        updateActivityProcess(activityProcessId, { progressPercentage: response.progressPercentage });
        updateProcess(uiProcessId, {
          progress: response.progressPercentage,
          status: response.status === 'completed' ? 'completed' : 'Processing labels...'
        });
      }

      // Check if completed or errored
      if (response.status === 'completed') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        // Auto-download if not already triggered
        if (!downloadTriggeredRef.current) {
          downloadTriggeredRef.current = true;
          await downloadFile(currentProcessId, activityProcessId, response.fileName);
        }

        // Update file status to completed
        setFiles(prev => prev.map(f => ({ ...f, status: 'completed' })));

        setSuccess('Labels processed successfully! Downloaded ZIP contains Collections, Barcodes, and Back Labels PDFs.');
        setResults({
          successCount: response.totalItems || 1,
          errorCount: response.failedItems || 0,
          filesGenerated: ['Collections PDF', 'Barcodes PDF', 'Back Labels PDF', 'Processing Report']
        });
        completeActivityProcess(activityProcessId, true);
        setProcessing(false);
        setLoading(false);

        setTimeout(() => {
          removeProcess(uiProcessId);
        }, 3000);
      } else if (response.status === 'error') {
        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          clearIntervalRef(activityProcessId);
        }

        setError('Processing failed. Please check the activity log for details.');
        setFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
        completeActivityProcess(activityProcessId, false);
        setProcessing(false);
        setLoading(false);

        setTimeout(() => {
          removeProcess(uiProcessId);
        }, 5000);
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  // Download file from server
  const downloadFile = async (currentProcessId, activityProcessId, fileName) => {
    try {
      const response = await fetch(API_ENDPOINTS.BILINGUAL_LABELS_DOWNLOAD(currentProcessId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'Bilingual_Labels.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      addLog(activityProcessId, `✓ ZIP downloaded: ${fileName}`);
    } catch (err) {
      console.error('Error downloading file:', err);
      addLog(activityProcessId, `✗ Error downloading file: ${err.message}`);
    }
  };

  const handleProcessLabels = async () => {
    // Validation
    if (files.length === 0) {
      setError('Please select a file to process');
      return;
    }

    if (selectedTab === 0) {
      // US validation
      const hasCountries = Object.values(usCountries).some(Boolean);
      const hasLanguage = usLanguage.trilingual || usLanguage.english;
      const hasCareInstruction = Object.values(usCareInstructions).some(Boolean);

      if (!hasCountries) {
        setError('Please select at least one country of origin');
        return;
      }
      if (!hasLanguage) {
        setError('Please select either TRILINGUAL or ENGLISH');
        return;
      }
      if (!hasCareInstruction) {
        setError('Please select a care instruction');
        return;
      }
    } else {
      // EU validation
      const hasCareInstruction = Object.values(euCareInstructions).some(Boolean);
      if (!hasCareInstruction) {
        setError('Please select a care instruction');
        return;
      }
    }

    setProcessing(true);
    setLoading(true);
    setError('');
    setSuccess('');
    setResults(null);
    downloadTriggeredRef.current = false;

    // Generate a unique process ID for activity log
    const activityProcessId = `bilingual-labels-${Date.now()}`;
    const regionType = selectedTab === 0 ? 'US' : 'EU';

    // Add to global activity log
    addActivityProcess(activityProcessId, `Back Labels Processing (${regionType})`, 'Back Labels');
    addLog(activityProcessId, `Starting Back Labels processing...`);
    addLog(activityProcessId, `Region: ${regionType}`);
    addLog(activityProcessId, `File: ${files[0].name}`);

    if (selectedTab === 0) {
      const selectedCountries = Object.entries(usCountries).filter(([_, v]) => v).map(([k]) => k.toUpperCase());
      const language = usLanguage.trilingual ? 'TRILINGUAL' : 'ENGLISH';
      addLog(activityProcessId, `Countries: ${selectedCountries.join(', ')}`);
      addLog(activityProcessId, `Language: ${language}`);
    }

    const uiProcessId = addProcess({
      name: 'Back Labels Processing',
      status: 'processing',
      progress: 0
    });

    try {
      const formData = new FormData();
      formData.append('file', files[0].file);
      formData.append('labelType', selectedTab === 0 ? 'US' : 'EU');

      if (selectedTab === 0) {
        formData.append('countries', JSON.stringify(usCountries));
        formData.append('language', JSON.stringify(usLanguage));
        formData.append('careInstructions', JSON.stringify(usCareInstructions));
      } else {
        formData.append('careInstructions', JSON.stringify(euCareInstructions));
        formData.append('countries', JSON.stringify({})); // Empty countries for EU
      }

      addLog(activityProcessId, `⏳ Uploading file to server...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 10 });
      updateProcess(uiProcessId, {
        progress: 10,
        status: 'Uploading file...'
      });

      // Update file status to processing
      setFiles(prev => prev.map(f => ({ ...f, status: 'processing' })));

      // Make API call to start processing
      const response = await apiCall(API_ENDPOINTS.BILINGUAL_LABELS_PROCESS, {
        method: 'POST',
        body: formData,
      });

      if (response.processId) {
        const serverProcessId = response.processId;
        processIdRef.current = serverProcessId;

        addLog(activityProcessId, `⏳ Processing started on server...`);

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus(serverProcessId, activityProcessId, uiProcessId);
        }, 1000);
        setIntervalRef(activityProcessId, intervalRef.current);
      } else {
        throw new Error('No process ID returned from server');
      }

    } catch (error) {
      console.error('Processing error:', error);
      updateProcess(uiProcessId, {
        status: 'error',
        progress: 0
      });

      addLog(activityProcessId, `✗ Error: ${error.message || 'Failed to process labels'}`);
      completeActivityProcess(activityProcessId, false);

      setError('Failed to process labels. Please check the file format and try again.');
      setFiles(prev => prev.map(f => ({ ...f, status: 'error' })));
      setProcessing(false);
      setLoading(false);

      setTimeout(() => {
        removeProcess(uiProcessId);
      }, 5000);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setError('');
    setSuccess('');
    setResults(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Back Labels" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          BACK LABELS
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} md={9}>
            <Paper elevation={1} sx={{ p: 3 }}>
              {/* Region Tabs */}
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider',
                  '& .MuiTab-root.Mui-selected': { color: TOOL_COLORS.PDF.primary },
                  '& .MuiTabs-indicator': { backgroundColor: TOOL_COLORS.PDF.primary }
                }}
              >
                <Tab
                  icon={<FaGlobeAmericas />}
                  label="US"
                  iconPosition="start"
                />
                <Tab
                  icon={<FaGlobeEurope />}
                  label="EU"
                  iconPosition="start"
                />
              </Tabs>

              {selectedTab === 0 ? (
                // US TAB
                <Box>
                  {/* Step 1: Select Care Instruction */}
                  <Card sx={{ mb: 3, border: `2px solid ${theme.palette.divider}` }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.text.primary }}>
                        STEP 1: Select A Care Instruction
                      </Typography>

                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                        If you select a care instruction you do not have to fill in 'Care Instruction' and 'Indoor/Outdoor Y/N'
                        columns in the sample file unless you want to override the care instructions.
                      </Typography>

                      {/* 4-Column Layout matching Desktop App exactly */}
                      <Grid container spacing={2}>
                        {/* Column 1: Countries */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            p: 2,
                            bgcolor: theme.palette.background.paper,
                            height: '100%'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: TOOL_COLORS.PDF.primary }}>
                              COUNTRIES OF ORIGIN
                            </Typography>
                            <FormGroup>
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.argentina} onChange={() => handleUsCountryChange('argentina')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">ARGENTINA</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.belgium} onChange={() => handleUsCountryChange('belgium')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">BELGIUM</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.china} onChange={() => handleUsCountryChange('china')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">CHINA</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.cambodia} onChange={() => handleUsCountryChange('cambodia')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">CAMBODIA</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.egypt} onChange={() => handleUsCountryChange('egypt')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">EGYPT</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.india} onChange={() => handleUsCountryChange('india')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">INDIA</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.turkey} onChange={() => handleUsCountryChange('turkey')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">TURKEY</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.ukraine} onChange={() => handleUsCountryChange('ukraine')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">UKRAINE</Typography>}
                              />
                              <FormControlLabel
                                control={<Checkbox checked={usCountries.vietnam} onChange={() => handleUsCountryChange('vietnam')} size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }} />}
                                label={<Typography variant="body2">VIETNAM</Typography>}
                              />
                            </FormGroup>
                          </Box>
                        </Grid>

                        {/* Column 2: Language */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            p: 2,
                            bgcolor: theme.palette.background.paper,
                            height: '100%'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: TOOL_COLORS.PDF.primary }}>
                              LANGUAGE OPTIONS
                            </Typography>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usLanguage.trilingual}
                                    onChange={() => handleUsLanguageChange('trilingual')}
                                    disabled={!usEnabled.trilingual}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">TRILINGUAL</Typography>}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usLanguage.english}
                                    onChange={() => handleUsLanguageChange('english')}
                                    disabled={!usEnabled.english}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">ENGLISH</Typography>}
                              />
                            </FormGroup>
                          </Box>
                        </Grid>

                        {/* Column 3: Care Instructions */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            p: 2,
                            bgcolor: theme.palette.background.paper,
                            height: '100%'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: TOOL_COLORS.PDF.primary }}>
                              CARE INSTRUCTIONS
                            </Typography>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usCareInstructions.indoorOutdoor}
                                    onChange={() => handleUsCareInstructionChange('indoorOutdoor')}
                                    disabled={!usEnabled.indoorOutdoor}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">INDOOR/OUTDOOR</Typography>}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usCareInstructions.washableNonHeat}
                                    onChange={() => handleUsCareInstructionChange('washableNonHeat')}
                                    disabled={!usEnabled.washableNonHeat}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">WASHABLE - NON HEAT DRY</Typography>}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usCareInstructions.washable}
                                    onChange={() => handleUsCareInstructionChange('washable')}
                                    disabled={!usEnabled.washable}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">WASHABLE</Typography>}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usCareInstructions.indoor}
                                    onChange={() => handleUsCareInstructionChange('indoor')}
                                    disabled={!usEnabled.indoor}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">INDOOR</Typography>}
                              />
                            </FormGroup>
                          </Box>
                        </Grid>

                        {/* Column 4: Reversible Options */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Box sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            p: 2,
                            bgcolor: theme.palette.background.paper,
                            height: '100%'
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: TOOL_COLORS.PDF.primary }}>
                              REVERSIBLE OPTIONS
                            </Typography>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usCareInstructions.outdoorReversible}
                                    onChange={() => handleUsCareInstructionChange('outdoorReversible')}
                                    disabled={!usEnabled.outdoorReversible}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">OUTDOOR REVERSIBLE</Typography>}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={usCareInstructions.indoorReversible}
                                    onChange={() => handleUsCareInstructionChange('indoorReversible')}
                                    disabled={!usEnabled.indoorReversible}
                                    size="small"
                                    sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                  />
                                }
                                label={<Typography variant="body2">INDOOR REVERSIBLE</Typography>}
                              />
                            </FormGroup>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Step 2: Import File */}
                  <Card sx={{ mb: 3, border: `2px solid ${theme.palette.divider}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                          STEP 2: Fill In Sample File And Import It
                        </Typography>
                        {files.length > 0 && (
                          <Tooltip title={`${files.length} file(s) selected`}>
                            <Visibility color="success" />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                        This will create a folder containing three PDFs: one for collections, one for barcodes, and one for back labels.
                      </Typography>

                      {/* Drag & Drop Zone */}
                      <Box
                        {...getRootProps()}
                        sx={{
                          border: `2px dashed ${isDragActive ? TOOL_COLORS.PDF.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          bgcolor: isDragActive
                            ? TOOL_COLORS.PDF.light
                            : files.length > 0
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                              : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          mb: 2,
                          '&:hover': {
                            borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                            bgcolor: files.length > 0
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                              : TOOL_COLORS.PDF.light,
                          }
                        }}
                      >
                        <input {...getInputProps()} />
                        {files.length === 0 ? (
                          <>
                            <CloudUpload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              Drop your Excel file here or click to browse
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Supports Excel (.xlsx) and CSV files
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              {files.length} file(s) selected
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click or drop to replace
                            </Typography>
                          </>
                        )}
                      </Box>

                      {/* File List */}
                      {files.length > 0 && (
                        <Box sx={{ maxHeight: 250, overflow: 'auto', mb: 2 }}>
                          {files.map((fileData) => (
                            <Box
                              key={fileData.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                                mb: 1,
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {fileData.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                                  ({formatFileSize(fileData.size)})
                                </Typography>
                                {fileData.status === 'processing' && (
                                  <Chip label="processing" size="small" color="primary" sx={{ ml: 1 }} />
                                )}
                                {fileData.status === 'completed' && (
                                  <Chip label="completed" size="small" color="success" sx={{ ml: 1 }} />
                                )}
                                {fileData.status === 'error' && (
                                  <Chip label="error" size="small" color="error" sx={{ ml: 1 }} />
                                )}
                              </Box>
                              {fileData.status === 'ready' && (
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile(fileData.id)}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  <Clear fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          onClick={() => handleDownloadTemplate('US')}
                          disabled={loading}
                          startIcon={<FaDownload />}
                          sx={{
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: alpha(theme.palette.text.primary, 0.7),
                            bgcolor: 'transparent',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2.5,
                            py: 0.8,
                            '&:hover': {
                              borderColor: alpha(theme.palette.text.primary, 0.4),
                              color: theme.palette.text.primary,
                              bgcolor: alpha(theme.palette.text.primary, 0.05),
                            },
                            '&:active': { transform: 'scale(0.98)' },
                          }}
                        >
                          Download Template
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={loading}
                          startIcon={<FaFilePdf />}
                          sx={{
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: alpha(theme.palette.text.primary, 0.7),
                            bgcolor: 'transparent',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2.5,
                            py: 0.8,
                            '&:hover': {
                              borderColor: alpha(theme.palette.text.primary, 0.4),
                              color: theme.palette.text.primary,
                              bgcolor: alpha(theme.palette.text.primary, 0.05),
                            },
                            '&:active': { transform: 'scale(0.98)' },
                          }}
                        >
                          Download Filled Back Label
                        </Button>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button
                          variant="contained"
                          onClick={handleProcessLabels}
                          disabled={files.length === 0 || processing}
                          sx={{ px: 4, bgcolor: TOOL_COLORS.PDF.primary, '&:hover': { bgcolor: TOOL_COLORS.PDF.hover } }}
                        >
                          {processing ? 'Processing Labels...' : 'Process Labels'}
                        </Button>

                        <Button
                          variant="outlined"
                          onClick={resetForm}
                          disabled={processing}
                          sx={{ borderColor: theme.palette.divider }}
                        >
                          Reset
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                // EU TAB
                <Box>
                  <Card sx={{ mb: 3, border: `2px solid ${theme.palette.divider}` }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.text.primary }}>
                        STEP 1: Select A Care Instruction
                      </Typography>

                      <Box sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        p: 2,
                        mb: 3
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: TOOL_COLORS.PDF.primary }}>
                          CARE INSTRUCTIONS
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={euCareInstructions.washableNonHeat}
                                  onChange={() => handleEuCareInstructionChange('washableNonHeat')}
                                  sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                />
                              }
                              label="WASHABLE - NON HEAT DRY"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={euCareInstructions.outdoorReversible}
                                  onChange={() => handleEuCareInstructionChange('outdoorReversible')}
                                  sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                />
                              }
                              label="OUTDOOR REVERSIBLE"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={euCareInstructions.washable}
                                  onChange={() => handleEuCareInstructionChange('washable')}
                                  sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                />
                              }
                              label="WASHABLE"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={euCareInstructions.indoorOutdoor}
                                  onChange={() => handleEuCareInstructionChange('indoorOutdoor')}
                                  sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                />
                              }
                              label="INDOOR/OUTDOOR"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={euCareInstructions.indoor}
                                  onChange={() => handleEuCareInstructionChange('indoor')}
                                  sx={{ '&.Mui-checked': { color: TOOL_COLORS.PDF.primary } }}
                                />
                              }
                              label="INDOOR"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Step 2: Import File */}
                  <Card sx={{ mb: 3, border: `2px solid ${theme.palette.divider}` }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Description sx={{ color: TOOL_COLORS.PDF.primary }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                          STEP 2: Fill In Sample File And Import It
                        </Typography>
                        {files.length > 0 && (
                          <Tooltip title={`${files.length} file(s) selected`}>
                            <Visibility color="success" />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                        This will create a folder containing three PDFs: one for collections, one for barcodes, and one for back labels.
                      </Typography>

                      {/* Drag & Drop Zone */}
                      <Box
                        {...getRootProps()}
                        sx={{
                          border: `2px dashed ${isDragActive ? TOOL_COLORS.PDF.primary : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          bgcolor: isDragActive
                            ? TOOL_COLORS.PDF.light
                            : files.length > 0
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                              : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          mb: 2,
                          '&:hover': {
                            borderColor: files.length > 0 ? '#4CAF50' : TOOL_COLORS.PDF.primary,
                            bgcolor: files.length > 0
                              ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                              : TOOL_COLORS.PDF.light,
                          }
                        }}
                      >
                        <input {...getInputProps()} />
                        {files.length === 0 ? (
                          <>
                            <CloudUpload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.PDF.primary : 'text.secondary', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              Drop your Excel file here or click to browse
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Supports Excel (.xlsx) and CSV files
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                            <Typography variant="body1" gutterBottom>
                              {files.length} file(s) selected
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Click or drop to replace
                            </Typography>
                          </>
                        )}
                      </Box>

                      {/* File List */}
                      {files.length > 0 && (
                        <Box sx={{ maxHeight: 250, overflow: 'auto', mb: 2 }}>
                          {files.map((fileData) => (
                            <Box
                              key={fileData.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                                mb: 1,
                                bgcolor: 'background.paper',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <Description sx={{ color: TOOL_COLORS.PDF.primary, fontSize: 20 }} />
                                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {fileData.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                                  ({formatFileSize(fileData.size)})
                                </Typography>
                                {fileData.status === 'processing' && (
                                  <Chip label="processing" size="small" color="primary" sx={{ ml: 1 }} />
                                )}
                                {fileData.status === 'completed' && (
                                  <Chip label="completed" size="small" color="success" sx={{ ml: 1 }} />
                                )}
                                {fileData.status === 'error' && (
                                  <Chip label="error" size="small" color="error" sx={{ ml: 1 }} />
                                )}
                              </Box>
                              {fileData.status === 'ready' && (
                                <IconButton
                                  size="small"
                                  onClick={() => removeFile(fileData.id)}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  <Clear fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          onClick={() => handleDownloadTemplate('EU')}
                          disabled={loading}
                          startIcon={<FaDownload />}
                          sx={{
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: alpha(theme.palette.text.primary, 0.7),
                            bgcolor: 'transparent',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2.5,
                            py: 0.8,
                            '&:hover': {
                              borderColor: alpha(theme.palette.text.primary, 0.4),
                              color: theme.palette.text.primary,
                              bgcolor: alpha(theme.palette.text.primary, 0.05),
                            },
                            '&:active': { transform: 'scale(0.98)' },
                          }}
                        >
                          Download Sample File
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={loading}
                          startIcon={<FaFilePdf />}
                          sx={{
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: alpha(theme.palette.text.primary, 0.7),
                            bgcolor: 'transparent',
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2.5,
                            py: 0.8,
                            '&:hover': {
                              borderColor: alpha(theme.palette.text.primary, 0.4),
                              color: theme.palette.text.primary,
                              bgcolor: alpha(theme.palette.text.primary, 0.05),
                            },
                            '&:active': { transform: 'scale(0.98)' },
                          }}
                        >
                          View Example
                        </Button>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button
                          variant="contained"
                          onClick={handleProcessLabels}
                          disabled={files.length === 0 || processing}
                          sx={{ px: 4, bgcolor: TOOL_COLORS.PDF.primary, '&:hover': { bgcolor: TOOL_COLORS.PDF.hover } }}
                        >
                          {processing ? 'Processing Labels...' : 'Process Labels'}
                        </Button>

                        <Button
                          variant="outlined"
                          onClick={resetForm}
                          disabled={processing}
                          sx={{ borderColor: theme.palette.divider }}
                        >
                          Reset
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Status & Instructions Panel */}
          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Information
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Processing labels and generating PDFs...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: TOOL_COLORS.PDF.primary } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaTags color={TOOL_COLORS.PDF.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Labels Processed"
                        secondary={`${results.successCount} file(s) successfully processed`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaFilePdf color={TOOL_COLORS.PDF.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary="PDFs Generated"
                        secondary="Collections, Barcodes, and Back Labels"
                      />
                    </ListItem>
                  </List>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Labels have been automatically downloaded. Check your downloads folder.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FaInfo color={theme.palette.info.main} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  INSTRUCTIONS
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                STEP 1: Select A Care Instruction
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                If you select a care instruction you do not have to fill in 'Care Instruction' and 'Indoor/Outdoor Y/N'
                columns in the sample file unless you want to override the care instructions.
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                STEP 2: Fill In Sample File And Import It
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                This will create a folder containing three PDFs: one for collections, one for barcodes, and one for back labels.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                NOTES:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                • The checkboxes are interconnected based on business rules
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                • Some countries are mutually exclusive (e.g., Argentina & Ukraine)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                • Language selection enables/disables certain care instructions
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                • TRILINGUAL supports all care instructions
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary }}>
                • ENGLISH does not support WASHABLE or OUTDOOR REVERSIBLE options
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary }}>
                • OUTDOOR REVERSIBLE is only enabled when TRILINGUAL + (WASHABLE or WASHABLE NON HEAT DRY or INDOOR/OUTDOOR)
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Generated Output:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip icon={<FaFilePdf />} label="Collections PDF" variant="outlined" size="small" />
                <Chip icon={<FaBarcode />} label="Barcodes PDF" variant="outlined" size="small" />
                <Chip icon={<FaTags />} label="Back Labels PDF" variant="outlined" size="small" />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Supported Languages:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip icon={<MdTranslate />} label="English" size="small" />
                <Chip icon={<MdTranslate />} label="Spanish" size="small" />
                <Chip icon={<MdTranslate />} label="French" size="small" />
                <Chip icon={<MdTranslate />} label="German" size="small" />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BilingualLabels;
